// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { getReferrerAttribution } from "@outschool/attribution";
import {
  OAUTH2_CAN_CREATE_USER,
  OAUTH2_CREATE_OPTIONS,
  OAUTH2_ERROR,
  OAUTH2_IS_NEW_USER,
  OAUTH2_ON_ERROR,
  OAUTH2_ON_SUCCESS,
  OAUTH2_REFRESH_TOKEN,
  OAUTH2_SESSION_TOKEN,
  OAUTH2_SHOULD_TRACK_LEARNER_CREATED,
  OAUTH2_USER_UID,
  decodeToken,
  isLearnerAuthTransfer
} from "@outschool/auth-shared";
import {
  CreateOrLoginError,
  LearnerAttributes
} from "@outschool/gql-backend-generated";
import {
  LoginOrCreateAccountWithAppleV2Mutation as LoginOrCreateAccountWithAppleV2MutationType,
  LoginOrCreateAccountWithAppleV2MutationVariables
} from "@outschool/gql-frontend-generated";
import { useLookupIP } from "@outschool/iplookup-client";
import {
  browseRootPath,
  joinThirdPartyAccountPath,
  redirectAfterLoginUrl
} from "@outschool/routes";
import * as Time from "@outschool/time";
import { useAnalytics, useTrackEvent } from "@outschool/ui-analytics";
import { useMutation } from "@outschool/ui-apollo";
import {
  AppleAuthResponse,
  PostLoginAction,
  SelfElectedStateFundingProgram,
  SupportedStrategies,
  getAuthStrategy,
  getNewUrlAfterLogin,
  getPostLoginPath,
  loginOrCreateAccountWithAppleMutationV2,
  persistPostLoginPath,
  setAuthStrategy,
  useOAuth2TrackingParams,
  useTokenContext,
  useTrackLearnerCreated
} from "@outschool/ui-auth";
import { useNavigation } from "@outschool/ui-utils";
import Cookies from "js-cookie";
import React from "react";

import { useAppState } from "../stores/AppStateProvider";

interface CreateParams {
  isGiftCardSignup?: boolean;
  isLeader?: boolean;
  trackingParameters?: { [key: string]: string };
  defaultLearnerAttributes?: LearnerAttributes;
  requiresMembershipForEnrollment: boolean;
  selfElectedStateFundingProgram?: SelfElectedStateFundingProgram;
}

interface AppleLoginSuccessPayload {
  sessionToken: string;
  refreshToken: string;
  postLoginPath: string;
  isLearnerTransfer: boolean;
}

export function useLoginWithApple(
  onSuccess: (payload: AppleLoginSuccessPayload) => void,
  onError: (error: CreateOrLoginError) => void,
  createParams?: CreateParams,
  postLoginPathParam?: string
) {
  const [loginWithAppleV2] = useMutation<
    LoginOrCreateAccountWithAppleV2MutationType,
    LoginOrCreateAccountWithAppleV2MutationVariables
  >(loginOrCreateAccountWithAppleMutationV2);
  const appState = useAppState();
  const trackEvent = useTrackEvent();
  const trackLearnerCreated = useTrackLearnerCreated();
  const analytics = useAnalytics();

  return React.useCallback(
    async (response: AppleAuthResponse) => {
      const { code } = response.authorization;
      const createOptions = {
        attribution: await analytics.attribution(),
        browserTimeZone: Time.guessBrowserTimeZone(),
        isGiftCardSignup: createParams?.isGiftCardSignup,
        isLeader: createParams?.isLeader,
        defaultLearnerAttributes: createParams?.defaultLearnerAttributes,
        requiresMembershipForEnrollment:
          createParams?.requiresMembershipForEnrollment ?? true,
        selfElectedStateFundingProgram:
          createParams?.selfElectedStateFundingProgram
      };
      const name = response.user
        ? `${response.user.name.firstName} ${response.user.name.lastName}`
        : undefined;
      const anonymousId = await analytics.anonymousId();
      const mutationResponse = await loginWithAppleV2({
        variables: {
          code,
          name,
          createOptions,
          osRef: {
            ...getReferrerAttribution(),
            anonymousId
          }
        }
      });
      const result = mutationResponse.data?.loginOrCreateAccountWithAppleV2;
      if (result?.__typename === "LoginError") {
        onError(result.error);
      } else {
        const {
          isNewUser,
          authentication: { sessionToken, refreshToken }
        } = result!;
        if (isNewUser) {
          if (createParams?.defaultLearnerAttributes) {
            const userUid = Cookies.get(OAUTH2_USER_UID);
            trackLearnerCreated({
              is_first_learner: true,
              parent_id: userUid
            });
            Cookies.remove(OAUTH2_USER_UID);
          }
          if (createParams?.trackingParameters) {
            trackEvent("signup-by-apple", createParams.trackingParameters);
          }
        }
        const decodedToken = decodeToken(sessionToken);
        const isLearnerTransfer = isLearnerAuthTransfer(decodedToken)
          ? decodedToken.isLearnerTransfer
          : false;
        const newUrlAfterLogin = getNewUrlAfterLogin();
        if (!isNewUser && newUrlAfterLogin) {
          persistPostLoginPath(newUrlAfterLogin);
        }
        const postLoginPath = isNewUser
          ? joinThirdPartyAccountPath()
          : newUrlAfterLogin ||
            postLoginPathParam ||
            appState.postLoginPath ||
            browseRootPath();

        // If user is signing up with Apple from the onboarding quiz, persist the post-login path (search page + filters)
        // so that it can be used after the joinThirdPartyAccountPath (unless there is already a post-login path set)
        if (
          !!createParams?.defaultLearnerAttributes &&
          !!postLoginPathParam &&
          !getPostLoginPath()
        ) {
          persistPostLoginPath(postLoginPathParam);
        }

        onSuccess({
          sessionToken,
          refreshToken,
          postLoginPath,
          isLearnerTransfer
        });
      }
    },
    [
      analytics,
      appState.postLoginPath,
      createParams?.isGiftCardSignup,
      createParams?.isLeader,
      createParams?.trackingParameters,
      createParams?.defaultLearnerAttributes,
      createParams?.requiresMembershipForEnrollment,
      loginWithAppleV2,
      onError,
      onSuccess,
      postLoginPathParam,
      trackEvent,
      createParams?.selfElectedStateFundingProgram,
      trackLearnerCreated
    ]
  );
}

// Exported here for easier test setup
export const setAuthStrategyWrapped = setAuthStrategy;

export function useLoginWithRedirect(
  onError: (error: CreateOrLoginError) => void,
  createParams?: CreateParams,
  postLoginAction?: PostLoginAction,
  postLoginPath?: string
) {
  const { getTokens } = useTokenContext();
  const appState = useAppState();
  const { setTrackingParameters } = useOAuth2TrackingParams();
  const { ipInfoLoaded, isInGDPR } = useLookupIP();
  const analytics = useAnalytics();

  React.useEffect(() => {
    const authError = Cookies.get(OAUTH2_ERROR);
    if (onError && authError) {
      Cookies.remove(OAUTH2_ERROR);
      onError(authError as CreateOrLoginError);
    }
  }, [onError]);

  React.useEffect(() => {
    async function fn() {
      // If user is signing up with Google from the onboarding quiz, persist the post-login path (search page + filters)
      // so that it can be used after the joinGoogleAccountPath (unless there is already a post-login path set)
      if (
        !!createParams?.defaultLearnerAttributes &&
        !!postLoginPath &&
        !getPostLoginPath()
      ) {
        persistPostLoginPath(postLoginPath);
      }
      Cookies.set(OAUTH2_ON_ERROR, window.location.href);
      Cookies.set(OAUTH2_ON_SUCCESS, redirectAfterLoginUrl());
      Cookies.set(OAUTH2_CAN_CREATE_USER, "true");
      Cookies.set(
        OAUTH2_CREATE_OPTIONS,
        JSON.stringify({
          attribution: await analytics.attribution(),
          browserTimeZone: Time.guessBrowserTimeZone(),
          isGiftCardSignup: createParams?.isGiftCardSignup,
          isLeader: createParams?.isLeader,
          subscribe: ipInfoLoaded ? !isInGDPR : false,
          defaultLearnerAttributes: createParams?.defaultLearnerAttributes,
          requiresMembershipForEnrollment:
            createParams?.requiresMembershipForEnrollment ?? true,
          selfElectedStateFundingProgram:
            createParams?.selfElectedStateFundingProgram
        })
      );
      const { sessionToken, refreshToken } = getTokens();
      if (sessionToken) {
        Cookies.set(OAUTH2_SESSION_TOKEN, sessionToken);
      } else {
        Cookies.remove(OAUTH2_SESSION_TOKEN);
      }
      if (refreshToken) {
        Cookies.set(OAUTH2_REFRESH_TOKEN, refreshToken);
      } else {
        Cookies.remove(OAUTH2_REFRESH_TOKEN);
      }
      if (createParams?.trackingParameters) {
        setTrackingParameters(createParams?.trackingParameters);
      }
      if (createParams?.defaultLearnerAttributes) {
        Cookies.set(OAUTH2_SHOULD_TRACK_LEARNER_CREATED, "true");
      }
    }
    fn();
  }, [
    analytics,
    appState,
    createParams?.isGiftCardSignup,
    createParams?.isLeader,
    createParams?.trackingParameters,
    createParams?.defaultLearnerAttributes,
    createParams?.requiresMembershipForEnrollment,
    createParams?.selfElectedStateFundingProgram,
    postLoginPath,
    setTrackingParameters,
    getTokens,
    ipInfoLoaded,
    isInGDPR
  ]);

  return React.useCallback(
    (strategy: SupportedStrategies) => {
      setAuthStrategyWrapped(
        strategy,
        postLoginAction || appState.postLoginAction,
        postLoginPath || appState.postLoginPath
      );
    },
    [appState, postLoginPath, postLoginAction]
  );
}

export function useTrackAndRedirectAfterLogin() {
  const appState = useAppState();
  const { trackingParameters, resetTrackingParameters } =
    useOAuth2TrackingParams();
  const trackEvent = useTrackEvent();
  const trackLearnerCreated = useTrackLearnerCreated();
  const navigate = useNavigation();

  React.useEffect(
    () => {
      const isNewUser = Boolean(Cookies.get(OAUTH2_IS_NEW_USER));
      Cookies.remove(OAUTH2_IS_NEW_USER);
      const { strategy } = getAuthStrategy();
      if (isNewUser) {
        trackEvent(`signup-by-${strategy}`, trackingParameters);
        if (Cookies.get(OAUTH2_SHOULD_TRACK_LEARNER_CREATED)) {
          const userUid = Cookies.get(OAUTH2_USER_UID);
          trackLearnerCreated({
            is_first_learner: true,
            parent_id: userUid
          });
          Cookies.remove(OAUTH2_SHOULD_TRACK_LEARNER_CREATED);
          Cookies.remove(OAUTH2_USER_UID);
        }
        if (strategy === "google") {
          navigate(joinThirdPartyAccountPath());
        } else {
          console.error("Unknown auth strategy:", strategy);
          appState.redirectAfterLogin(browseRootPath());
        }
      } else {
        const newUrlAfterLogin = getNewUrlAfterLogin();
        if (newUrlAfterLogin) {
          persistPostLoginPath(newUrlAfterLogin);
          setAuthStrategy(strategy, undefined, newUrlAfterLogin);
        }
        /**
         * TODO: find how to handle post login action.
         * postLoginAction is defined in the localStorage,
         * but it can't be used because _handleLoggedIn is undefined by the time this method is called.
         */
        appState.redirectAfterLogin(browseRootPath());
      }
      resetTrackingParameters();
    },
    // Only run once on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
}
